import React, { memo, useEffect, useRef, useState } from 'react'
import { DropdownField,InputField } from "components/forms/reduxForm";
import Checkbox from 'components/forms/Checkbox';
import Button from 'components/shared/Button';
import Drawer, { DrawerAction } from 'components/shared/Drawer';
import strings from 'strings';
import { REPORT_TYPE } from 'constants/constantVariables';
import { Typography } from '@material-ui/core';
import Tooltip from '@material-ui/core/Tooltip';
import Loader from 'components/shared/Loader';
import Chip from '@material-ui/core/Chip';
import dayjs from 'dayjs';

const frequencyOptions = [
    { value: 'EVERY_1_MONTH', label: 'Every 1 month', max: 12, months: 1 },
    { value: 'EVERY_2_MONTHS', label: 'Every 2 months', max: 6, months: 2 },
    { value: 'EVERY_3_MONTHS', label: 'Every 3 months', max: 4, months: 3 },
    { value: 'EVERY_4_MONTHS', label: 'Every 4 months', max: 3, months: 4 },
    { value: 'EVERY_6_MONTHS', label: 'Every 6 months', max: 2, months: 6 },
    { value: 'EVERY_12_MONTHS', label: 'Every 1 year', max: 1, months: 12 }
];

// Months-per-period for a frequency value. Reads the table above instead of regex-parsing
// a digit out of the enum name (e.g. 'EVERY_6_MONTHS' -> 6), which coupled the math to the
// exact naming convention of the enum.
const getMonthsForFrequency = (frequency) => {
    const option = frequencyOptions.find(opt => opt.value === frequency);
    return option ? option.months : 1;
};

const monitoringStrings = strings.MONITORINGS;

export const ReportsMonitoringDrawerLegacy = memo((props) => {
    const {
        open,
        onClose,
        getMonitoringConfig,
        updateMonitoringConfig,
        roxAppId,
        handleSubmit,
        monitoringStatus,
        getApplication
    } = props

    const [previewLoading, setPreviewLoading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState(false);
    // The config for THIS drawer's applicant, captured from the request's own response rather than read
    // from the shared Redux monitoringConfig slot (which is keyed only by app and can be overwritten by a
    // slower in-flight request for a different applicant). reqIdRef ignores stale responses.
    const [localConfig, setLocalConfig] = useState(null);
    const reqIdRef = useRef(0);

    // Fetch monitoring config when drawer opens
    useEffect(() => {
        const myReqId = ++reqIdRef.current;
        const fetchConfig = async () => {
            setLoading(true);
            setLoadError(false);
            try {
                const res = await getMonitoringConfig(roxAppId);
                if (reqIdRef.current !== myReqId) return; // a newer applicant's request superseded this one
                setLocalConfig(res && res.response ? res.response : null);
            } catch (e) {
                // Flag an error and block the form rather than render/save a stale config under this
                // roxAppId. The request helper handles the error toast.
                if (reqIdRef.current === myReqId) setLoadError(true);
            } finally {
                if (reqIdRef.current === myReqId) setLoading(false);
            }
        };
        if (open && roxAppId) {
            setLocalConfig(null);
            fetchConfig();
        }
    }, [open, roxAppId, getMonitoringConfig]);

    const reports = localConfig?.reports || [];

    // Initialize checked state for each report
    const [checkedReports, setCheckedReports] = useState([]);

    useEffect(() => {
        if (open && reports.length) {
            setCheckedReports(reports.map(report => !!report.enabled));
        }
    }, [open, reports]);

    const [dayOfTheMonth, setDayOfTheMonth] = useState(1);
    const [iterations, setIterations] = useState(1);
    const [selectedFrequency, setSelectedFrequency] = useState(frequencyOptions[0].value);
    const [endMonitoringDate, setEndMonitoringDate] = useState(null);
    const [nextMonitoringDate, setNextMonitoringDate] = useState(null);

    // Calculate end monitoring date when frequency, iterations, or dayOfMonth changes
    useEffect(() => {
        if (!selectedFrequency || !iterations || !dayOfTheMonth) {
            setEndMonitoringDate(null);
            return;
        }
        const monthsInterval = getMonthsForFrequency(selectedFrequency);
        const totalMonths = monthsInterval * iterations;
        const start = dayjs().date(dayOfTheMonth);
        const end = start.add(totalMonths, 'month');
        setEndMonitoringDate(end.format('YYYY-MM-DD'));
    }, [selectedFrequency, iterations, dayOfTheMonth]);

    // Calculate next monitoring date when frequency or dayOfMonth changes
    useEffect(() => {
        if (!selectedFrequency || !dayOfTheMonth) {
            setNextMonitoringDate(null);
            return;
        }
        const monthsInterval = getMonthsForFrequency(selectedFrequency);
        const next = dayjs().date(dayOfTheMonth).add(monthsInterval, 'month');
        setNextMonitoringDate(next.format('YYYY-MM-DD'));
    }, [selectedFrequency, dayOfTheMonth]);

    // Add this effect to trigger initial calculation when drawer opens
    useEffect(() => {
        if (open) {
            // Initialize state from this applicant's loaded config if available
            const initialDay = localConfig?.dayOfTheMonth ?? 1;
            const initialIterations = localConfig?.iterations ?? 1;
            const initialFrequency = localConfig?.frequency ?? frequencyOptions[0].value;

            setDayOfTheMonth(initialDay);
            setIterations(initialIterations);
            setSelectedFrequency(initialFrequency);

            // Calculate dates based on initialized values
            const monthsInterval = getMonthsForFrequency(initialFrequency);
            const totalMonths = monthsInterval * initialIterations;
            const start = dayjs().date(initialDay);
            const end = start.add(totalMonths, 'month');
            setEndMonitoringDate(end.format('YYYY-MM-DD'));

            const next = start.add(monthsInterval, 'month');
            setNextMonitoringDate(next.format('YYYY-MM-DD'));
        }
    }, [open, localConfig]);

    const handleCheckboxChange = (index, checked) => {
        const updated = [...checkedReports];
        updated[index] = checked;
        setCheckedReports(updated);
    };

    const handleProceed = (data) => {
        setPreviewLoading(true);

        const entityConfigurations = reports.map((report, idx) => ({
            reportType: report.reportType,
            entityId: report.entityId,
            enabled: checkedReports[idx]
        }));

        const anyEnabled = entityConfigurations.some(config => config.enabled === true);

        const payload = {
            iterations: iterations,
            dayOfTheMonth: dayOfTheMonth,
            frequency: selectedFrequency,
            enabled: anyEnabled,
            entityConfigurations
        };

        updateMonitoringConfig(roxAppId, payload)
            .then(() => {
                onClose();
                getApplication()
            })
            .finally(() => {
                setPreviewLoading(false);
            });
    };

    function getLabel(report) {
        if (report.reportType === REPORT_TYPE.UCC){
            return report.reportType + " (" + report.entityName + ")"
        }
        if (report.reportType === REPORT_TYPE.LEXIS_VERIFICATION){
            return monitoringStrings.VERIFICATION
        }
        if (report.reportType === REPORT_TYPE.LEXIS_BACKGROUND){
            return monitoringStrings.BACKGROUND_CHECK + " (" + report.entityName + ")"
        }
        return report.reportType;
    }

    const validateDayOfMonth = value => {
        if (value === undefined || value === null || value === '') return 'Required';
        const num = Number(value);
        if (isNaN(num) || num < 1 || num > 31) return 'Must be between 1 and 31';
        return undefined;
    };

    const validateIterations = (value, allValues) => {
        if (value === undefined || value === null || value === '') return 'Required';
        const num = Number(value);
        const freqValue = allValues && allValues.frequency ? allValues.frequency : selectedFrequency;
        const freqObj = frequencyOptions.find(opt => opt.value === freqValue);
        const max = freqObj ? freqObj.max : 12;
        if (isNaN(num) || num < 1) return 'Must be at least 1';
        if (num > max) return `Maximum for this frequency is ${max}`;
        return undefined;
    };

    return (
        <Drawer
            open={open}
            onClose={onClose}
            title={monitoringStrings.TITLE}
            actions={
                <div className='d-flex align-items-center'>
                    <Chip
                        label={monitoringStatus ? monitoringStrings.ENABLED : monitoringStrings.DISABLED}
                        variant="outlined"
                        style={{
                            marginRight: 8,
                            backgroundColor: 'transparent',
                            color: monitoringStatus ? '#4caf50' : undefined,
                            borderColor: monitoringStatus ? '#4caf50' : undefined
                        }}
                    />
                </div>
            }
        >
            <div className='posting-container'>
                {loading ? (
                    <Loader />
                ) : loadError ? (
                    <div className='mb-2'>
                        Couldn’t load monitoring configuration. Please close and reopen to try again.
                    </div>
                ) : (
                    <>
                        {reports.map((report, index) => (
                            <div key={report.id} className='d-flex align-items-center justify-content-between mb-2'>
                                <div className='d-flex align-items-center'>
                                    {report.available ? (
                                        <Checkbox
                                            label={
                                                getLabel(report)
                                            }
                                            className='custom-text'
                                            checked={checkedReports[index]}
                                            onChange={checked => handleCheckboxChange(index, checked)}
                                            disabled={false}
                                        />
                                    ) : (
                                        <Tooltip title={report.message || ''} arrow>
                                            <span>
                                                <Checkbox
                                                    label={
                                                        getLabel(report)
                                                    }
                                                    className='custom-text'
                                                    checked={checkedReports[index]}
                                                    onChange={checked => handleCheckboxChange(index, checked)}
                                                    disabled={true}
                                                />
                                            </span>
                                        </Tooltip>
                                    )}
                                </div>
                            </div>
                        ))}
                        <div>
                            <Typography>{monitoringStrings.CHOOSE_FREQUENCY}</Typography>
                            <DropdownField
                                name="frequency"
                                label={monitoringStrings.FREQUENCY_LABEL}
                                placeholder={monitoringStrings.FREQUENCY_PLACEHOLDER}
                                options={frequencyOptions}
                                isClearable={false}
                                style={{ marginBottom: "16px", width: "30%" }}
                                onChange={option => {
                                    setSelectedFrequency(option);
                                }}
                                value={frequencyOptions.find(opt => opt.value === selectedFrequency) || null}
                                required
                            />
                            <InputField
                                name='dayOfTheMonth'
                                label="Day of the month"
                                type="number"
                                validate={validateDayOfMonth}
                                value={dayOfTheMonth}
                                onChange={e => setDayOfTheMonth(Math.max(1, Math.min(31, Number(e.target.value))))}
                                style={{ marginBottom: "16px", width: "30%" }}
                                required
                            />
                            {endMonitoringDate && (
                                <Typography variant="body2" style={{ marginBottom: "16px" }}>
                                    Next monitoring date: <b>{nextMonitoringDate}</b>
                                </Typography>
                            )}
                            <InputField
                                name='iterations'
                                label="Iterations"
                                type="number"
                                value={iterations}
                                validate={validateIterations}
                                onChange={e => {
                                    setIterations(Math.max(1, Math.min(12, Number(e.target.value))));
                                }}
                                style={{ marginBottom: "16px", width: "30%" }}
                                required
                            />
                            {endMonitoringDate && (
                                <Typography variant="body2" style={{ marginBottom: "16px" }}>
                                    End monitoring date: <b>{endMonitoringDate}</b>
                                </Typography>
                            )}
                        </div>
                        <Typography>{monitoringStrings.ADDITIONAL_CHARGES}</Typography>
                    </>
                )}
            </div>
            <DrawerAction>
                <Button
                    variant='text'
                    onClick={onClose}
                >
                    {monitoringStrings.CANCEL}
                </Button>
                <Button
                    loading={previewLoading}
                    onClick={handleSubmit((data) => handleProceed(data))}
                    disabled={loading || loadError || previewLoading}
                >
                    {monitoringStrings.PROCEED}
                </Button>
            </DrawerAction>
        </Drawer>
    )
})